/** Workflows (M26): list, builder, runs and statistics. */
import { workflowsP3 } from "./workflows-p3";
import { workflowsTriggers } from "./workflows-triggers";
import { workflowsConnectors } from "./workflows-p5-connectors";
import { workflowsConnectorStep } from "./workflows-connector";
import { workflowsContent } from "./workflows-content";
import { workflowsDefaults } from "./workflows-defaults";
import { workflowsSystem } from "./workflows-system";
import { workflowsService } from "./workflows-service";
import { workflowsCustomer } from "./workflows-customer";
import { workflowsConversation } from "./workflows-conversation";
import { workflowsP4 } from "./workflows-p4";
import { workflowsAddStep } from "./workflows-add-step";
import { workflowsExtras } from "./workflows-extras";
import { workflowsFollowups } from "./workflows-followups";
import { workflowsOutcomes } from "./workflows-outcomes";
import { workflowsSetup } from "./workflows-setup";
import { workflowsVerify } from "./workflows-verify";
import { workflowsRegression } from "./workflows-regression";

export const workflows = {
  title: "Workflows",
  subtitle: "Decide what happens, when, and for whom. A workflow starts on a trigger, checks who it is for, then runs its steps — tag, assign, notify your team, or send a webhook only when it matters.",
  planNeeded: "{{plan}} plan",
  planFeature: "Workflows",
  newWorkflow: "New workflow",
  empty: {
    title: "No workflows yet",
    body: "Start with a trigger, such as a booking being confirmed or a lead being captured, and choose what happens next.",
  },
  kpis: {
    live: "Live workflows",
    liveOf: "{{count}} of {{limit}} on your plan",
    liveUnlimited: "No limit on your plan",
    runs: "Runs (30 days)",
    completed: "Completed",
    failed: "Failed runs",
  },
  filters: {
    agent: "Agent",
    allAgents: "All agents",
    none: "No workflows for this agent yet.",
  },
  loadFailed: "We couldn't load your workflows.",
  notFound: {
    title: "We couldn't find that workflow",
    body: "It may have been deleted, or the link is not right.",
  },
  groups: {
    start: "Start of conversation",
    during: "During conversation",
    staff: "Staff and routing",
    leads: "Leads and bookings",
    other: "Reusable and other",
    ...workflowsTriggers.groups,
  },
  groupNote: "Workflows of one trigger run in this order.",
  columns: { title: "Title", kind: "Kind", state: "State", channels: "Channels", updated: "Updated", sent: "Sent", completed: "Completed", failed: "Failed", order: "Order", ...workflowsP4.columns },
  goalTitle: workflowsP4.goalTitle,
  moveUp: "Move {{name}} up",
  moveDown: "Move {{name}} down",
  reordered: "Order saved",
  allChannels: "All channels",
  kinds: {
    facing: "Customer-facing",
    background: "Background",
    facingOnly: "Customer-facing",
    either: "Customer-facing or background",
    ...workflowsTriggers.kinds,
  },
  facingNote: "Only the first matching customer-facing workflow runs. Move one up to run it first.",
  mixedNote: "Customer-facing: only the first match runs. Background workflows all run, in this order.",
  states: { draft: "Draft", live: "Live", paused: "Paused" },
  unpublished: "Unpublished changes",
  updatedBy: "{{when}} · {{name}}",
  triggers: {
    ...workflowsP3.triggers,
    ...workflowsTriggers.triggers,
    ...workflowsP4.triggers,
    widget_opened: "Visitor opens the widget",
    home_option: "Visitor picks a Home option",
    visitor_first_message: "Visitor sends first message",
    visitor_message: "Visitor sends any message",
    assistant_replied: "Assistant replied",
    assistant_could_not_answer: "Assistant could not answer",
    intent_detected: "Visitor intent detected",
    staff_replied: "Staff replied",
    feedback_negative: "Visitor gave negative feedback",
    handoff_requested: "Handoff requested",
    conversation_assigned: "Conversation assigned",
    conversation_status_changed: "Conversation status changed",
    lead_created: "Lead captured",
    lead_updated: "Lead updated by staff",
    booking_requested: "Booking requested",
    booking_confirmed: "Booking confirmed",
    booking_declined: "Booking declined",
    booking_cancelled: "Booking cancelled",
    action_run_finished: "Action run finished",
    source_processed: "Knowledge source synced",
    source_failed: "Knowledge source failed",
  },
  triggerHints: {
    ...workflowsP3.triggerHints,
    ...workflowsTriggers.triggerHints,
    ...workflowsP4.triggerHints,
    widget_opened: "Greet with reply buttons before they type.",
    home_option: "Runs when a Widget Home card is tapped.",
    assistant_could_not_answer: "Offer a person, a booking or a lead form.",
    intent_detected: "Booking, pricing, talk to a person … before the answer.",
    action_run_finished: "Follow up on a custom action's result.",
    visitor_first_message: "Triage or route the first question of a conversation.",
    visitor_message: "Tag or forward every visitor message.",
    assistant_replied: "React to the assistant's answers.",
    staff_replied: "React to replies from your team.",
    feedback_negative: "Follow up when an answer was not helpful.",
    handoff_requested: "Route, notify and alert your chat apps.",
    conversation_assigned: "React to a new assignee or team.",
    conversation_status_changed: "Run steps when a conversation is resolved or closed.",
    lead_created: "Assign an owner, sync a CRM, alert sales.",
    lead_updated: "React when staff change a stage or owner.",
    booking_requested: "Tell your team about a new request.",
    booking_confirmed: "Send only confirmed bookings to your systems.",
    booking_declined: "Follow up on declined bookings.",
    booking_cancelled: "Clean up after a cancellation.",
    source_processed: "Tell your team when a sync finishes.",
    source_failed: "Get alerted when a sync fails.",
  },
  create: {
    title: "Create a workflow",
    subtitle: "Choose the trigger. It can't be changed later.",
    name: "Name",
    namePlaceholder: "e.g. Confirmed tours to the registrar",
    nameRequired: "Give the workflow a name.",
    nameTooLong: "Keep the name to {{max}} characters or fewer.",
    submit: "Create workflow",
    created: "Workflow created as a draft",
    mode: "Start from",
    fromTrigger: "A trigger",
    fromTemplate: "A template",
    templatesTitle: "Start from a template",
    starter: "Hi! How can we help you today?",
  },
  templates: {
    title: "Templates",
    note: "Every template opens as a draft. Nothing runs until you set it live.",
    ...workflowsP4.templates,
  },
  builder: {
    customSchedule: workflowsP3.customSchedule,
    options: { ...workflowsP3.options, ...workflowsTriggers.options },
    tabs: { ...workflowsP4.tabs, tests: workflowsRegression.tab },
    back: "Back to workflows",
    heading: "Workflow builder",
    more: "More",
    save: "Save draft",
    saved: "Draft saved",
    publish: "Publish",
    published: "Published. New events run version {{version}}.",
    pause: "Pause",
    paused: "Paused. Runs in progress finish.",
    resume: "Resume",
    resumed: "Live again",
    delete: "Delete",
    deleteLive: "Delete (pause it first)",
    deleteTitle: "Delete {{name}}?",
    deleteBody: "Its runs and statistics are deleted too. This can't be undone.",
    deleted: "Workflow deleted",
    problems: "Fix these before saving:",
    unsaved: "You have unsaved changes.",
    readOnly: "You can view this workflow. Ask an admin to change it.",
    cannotPublish: "Only members who can publish workflows can set them live.",
    version: "v{{version}}",
    trigger: "Trigger",
    settings: "Who and when",
    agents: "Agents",
    allAgents: "All agents",
    channels: "Channels",
    schedule: "When",
    schedules: { any: "Any time", office_hours: "During office hours", outside_office_hours: "Outside office hours", custom: "Custom weekly schedule" },
    conditions: "Only when",
    conditionsHint: "Leave empty to run for everything this trigger fires on.",
    intentHint: "Choose which intent starts this workflow: add a condition on “Detected intent” ({{actions}}) or “Detected topic”.",
    path: "Path {{id}}",
    pathFrom: "Starts at the trigger",
    addPath: "Add path",
    removePath: "Remove Path {{id}}",
    pathLabel: "Path name",
    addStep: "Add step",
    noSteps: "No steps yet. Add the first one.",
    editStep: "Edit step",
    removeStep: "Remove step",
    moveStepUp: "Move step up",
    moveStepDown: "Move step down",
    done: "Done",
    stepName: "Step name",
    stepNameHint: "Used in run logs. Letters, numbers and _.",
    stepNameInvalid: "Letters, numbers, spaces and _ only.",
    incomplete: {
      ...workflowsP3.incomplete,
      ...workflowsP4.incomplete,
      ...workflowsConnectorStep.incomplete,
      ...workflowsConversation.incomplete,
      ...workflowsContent.incomplete,
      ...workflowsService.incomplete,
      ...workflowsSystem.incomplete,
      message: "Write what the visitor sees",
      buttonLabels: "Every button needs a label",
      buttonPaths: "Choose where each button leads",
      fieldLabel: "Name the field",
      options: "Add the choices",
      saveKey: "Name the field it is saved to",
      action: "Choose which action to show",
      person: "Choose who to hand over to",
    },
    preview: {
      open: "Preview",
      title: "Preview · draft",
      description: "A test conversation with your draft. Webhooks are not sent and nothing is counted.",
      banner: "Preview — webhooks, emails and notifications are not sent.",
      agent: "Agent",
      agentHint: "The test conversation runs with this agent.",
      restart: "Restart",
      loading: "Starting the preview",
      failedTitle: "The preview could not start",
      waitsForMessage: "This workflow starts when a visitor writes. Type a message below to try it.",
      noRun: "No workflow step ran for this message. Its conditions did not match, or the assistant answered it as usual.",
      notStarted: "The workflow did not start. Check who and when it runs for, then restart.",
      conversation: "Preview conversation",
      placeholder: "Write as the visitor",
      footer: "Only an assistant answer uses test credits. Workflow steps use none.",
      pathTaken: "Path taken: {{path}}",
      outcome: workflowsOutcomes.preview.outcome,
      dry: {
        title: "Dry run · draft",
        description: "The steps this workflow would take for sample facts. Nothing is sent or changed.",
        banner: "Dry run — nothing is sent or changed.",
        facts: "Sample facts",
        channel: "Channel",
        officeHours: "Office hours",
        open: "Open",
        closed: "Closed",
        message: "Visitor message (optional)",
        messageHint: "Used by conditions that read the message text.",
        run: "Show steps",
        failedTitle: "The dry run could not finish",
        runs: "This workflow would run for these facts.",
        wouldNot: "This workflow would not start for these facts.",
        mismatch: {
          agent: "It is set for other agents.",
          channel: "It is not set for this channel.",
          schedule: "Its schedule does not match the office hours you chose.",
          conditions: "Its “Only when” conditions do not match.",
        },
        paths: "Paths taken: {{paths}}",
        pathHeading: "Path {{id}}",
        pathHeadingNamed: "Path {{id}} · {{label}}",
        branch: "Takes the branch “{{label}}”",
        branchElse: "No branch matches: takes the else path",
        rules: "Rules that match: {{rules}}",
        rulesNone: "No rule matches",
        goTo: "Continues on Path {{path}}",
        ended: "The run ends here",
        snoozedUntil: "Snoozes the conversation until {{time}}",
        snoozeSkipped: "Nothing to snooze for, so it carries on: {{reason}}",
        waitSkipped: "Nothing to wait for, so it carries on: {{reason}}",
        waitsUntil: "Waits until {{time}}, when the office opens",
        snoozeSkips: { past: "that moment has already passed", office_open: "the office is open now", no_office_hours: "there are no office hours to wait for" },
        inRule: "From the rule “{{rule}}”",
        tooLong: "The walk stopped at the step limit.",
        empty: "No steps would run.",
        steps: "Steps in order",
      },
    },
    channelNotes: {
      title: "How channels show customer-facing steps",
      messenger: "Messenger shows reply buttons as quick replies: at most {{replies}}, with labels cut to {{chars}} characters. Set a Messenger label on long buttons.",
      email: "Email shows reply buttons as numbered options; the visitor answers with the number. Collect data steps are skipped on email.",
      api: "The developer API does not run customer-facing steps: a conversation over the API goes straight to the assistant.",
      steps: { ...workflowsConversation.builder.channelNotes.steps, ...workflowsVerify.builder.channelNotes.steps, ...workflowsContent.channelNotes.steps },
    },
  },
  stepGroups: {
    visitorLocked: "Needs a trigger the visitor can see, such as “Visitor opens the widget”.",
    waitLocked: "This step needs a conversation, so it is not available on triggers that run before the visitor writes.",
  },
  steps: {
    ...workflowsP3.steps,
    ...workflowsP4.steps,
    ...workflowsConnectorStep.steps,
    ...workflowsConversation.steps,
    ...workflowsContent.steps,
    ...workflowsVerify.steps,
    ...workflowsSystem.steps,
    ...workflowsService.steps,
    send_webhook: "Send webhook",
    add_tags: "Add tags",
    remove_tags: "Remove tags",
    assign: "Assign",
    set_status: "Set status",
    add_note: "Add internal note",
    update_lead: "Update lead",
    notify_staff: "Notify staff",
    send_message: "Send message",
    reply_buttons: "Reply buttons",
    collect_data: "Collect data",
    let_assistant_answer: "Let the assistant answer",
    show_booking: "Show booking card",
    show_lead_form: "Show lead form",
    hand_to_person: "Hand to a person",
    expected_reply_time: "Show expected reply time",
    branch: "Branch",
    apply_rules: "Apply rules",
    go_to_path: "Go to path",
    end: "End",
  },
  summaries: {
    ...workflowsP3.summaries,
    ...workflowsP4.summaries,
    ...workflowsConnectorStep.summaries,
    ...workflowsConversation.summaries,
    ...workflowsContent.summaries,
    ...workflowsVerify.summaries,
    ...workflowsFollowups.summaries,
    ...workflowsSystem.summaries,
    send_webhook: "To {{endpoint}}",
    tags: "{{tags}}",
    assignTeam: "To team {{name}}",
    assignUser: "To {{name}}",
    unassign: "Clear the assignee",
    set_status: "Mark as {{status}}",
    update_lead: "{{changes}}",
    leadStage: "Stage → {{stage}}",
    leadOwner: "Owner → {{name}}",
    leadSync: "Sync to CRM",
    notify: "{{who}}: {{title}}",
    branch_one: "{{count}} branch · else → {{else}}",
    branch_other: "{{count}} branches · else → {{else}}",
    rules_one: "{{count}} rule",
    rules_other: "{{count}} rules",
    go_to_path: "Continue on Path {{path}}",
    end: "Stop here",
    endRun: "end",
    unknown: "Not set",
    collect: "{{question}} ({{type}}, saved to {{target}})",
    answer: "Answers from your knowledge, with sources",
    answerGuided: "Answers from your knowledge · {{guidance}}",
    booking: "Booking card · {{action}}",
    leadForm: "Lead form · {{action}}",
    handDefault: "Default routing",
    replyTime: "Tells the visitor when to expect a reply",
    typedInstead: "Typed instead → the assistant answers",
    exits: {
      ...workflowsP3.exits,
      ...workflowsP4.exits,
      ...workflowsConnectorStep.exits,
      ...workflowsConversation.exits,
      ...workflowsVerify.exits,
      ...workflowsFollowups.exits,
      ...workflowsTriggers.exits,
      to: "{{label}} → {{path}}",
      branch: "Branch {{n}}",
      else: "Else",
      button: "Button {{n}}",
      answered: "Answered",
      notAnswered: "Could not answer",
      askedForPerson: "Asked for a person",
      booked: "Booked or requested",
      notBooked: "Not booked",
      submitted: "Details sent",
    },
  },
  fields: {
    ...workflowsP3.fields,
    ...workflowsP4.fields,
    ...workflowsConversation.fields,
    ...workflowsVerify.fields,
    ...workflowsFollowups.fields,
    ...workflowsSystem.fields,
    endpoint: "Webhook endpoint",
    endpointHint: "Signed, retried and logged like any webhook. Manage endpoints in Settings → Webhooks.",
    noEndpoints: "No webhook endpoints yet.",
    openWebhooks: "Add one in Settings",
    body: "Body",
    bodies: { standard: "Standard event", custom: "Custom JSON" },
    template: "Custom JSON",
    templateHint: "Put every variable inside quotes, as in the example. Values are escaped for you.",
    message: "Chat message (Slack / Discord)",
    messageHint: "What Slack and Discord endpoints show. Variables work here.",
    variables: "Variables",
    tags: "Tags",
    tagsHint: "Comma-separated.",
    tagsCut: "Up to {{max}} tags of {{chars}} characters each. The rest were left out.",
    assignTo: "Assign to",
    assignTargets: { team: "A team", user: "A person", unassign: "Nobody (clear)" },
    team: "Team",
    member: "Member",
    choose: "Choose…",
    status: "Status",
    statuses: { open: "Open", waiting: "Waiting", resolved: "Resolved", closed: "Closed" },
    note: "Note",
    noteHint: "Visible to staff only. Variables work here.",
    stage: "Move to stage",
    owner: "Set owner",
    unchanged: "Leave unchanged",
    syncCrm: "Sync to the connected CRM or Google Sheet",
    notifyWho: "Notify",
    notifyTargets: { owners_admins: "Owners and Admins", assignee: "The assignee", lead_owner: "The lead's owner", team: "A team", user: "A person" },
    title: "Title",
    notifyBody: "Message",
    email: "Also send an email",
    branchLabel: "Branch name",
    goTo: "Then go to",
    elseGoTo: "If nothing matches",
    endHere: "End the run",
    addBranch: "Add branch",
    removeBranch: "Remove branch",
    ruleLabel: "Rule name",
    addRule: "Add rule",
    removeRule: "Remove rule",
    ruleActions: "Do",
    addAction: "Add action",
    newPath: "New path",
    choosePath: "Choose a path",
    visitorMessage: "Message",
    messageMarkdownHint: "Bold, italic and links work.",
    buttons: "Buttons ({{min}}–{{max}})",
    buttonLabelN: "Button {{n}} label",
    buttonGoesToN: "Button {{n}} goes to",
    moveButtonUp: "Move button {{n}} up",
    moveButtonDown: "Move button {{n}} down",
    removeButtonN: "Remove button {{n}}",
    addButton: "Add button",
    messengerLabelN: "Button {{n}} Messenger label",
    messengerLabelPlaceholder: "Shorter label for Messenger",
    removeMessengerLabelN: "Remove the Messenger label of button {{n}}",
    messengerCut: "Messenger cuts this to {{max}} characters.",
    setMessengerLabel: "Set a Messenger label",
    allowTyping: "Let the visitor type instead",
    allowTypingHint: "A typed message goes to the assistant.",
    question: "Question",
    fieldLabel: "Field label",
    fieldLabelHint: "Shown on the input in the chat.",
    answerType: "Answer type",
    answerTypeFixed: "Set by where the answer is saved.",
    fieldTypes: { text: "Short text", email: "Email", phone: "Phone", number: "Number", date: "Date", dropdown: "Dropdown" },
    saveTo: "Save the answer to",
    saveTargets: { visitor_name: "Visitor name", visitor_email: "Visitor email", visitor_phone: "Visitor phone", lead: "A lead field", var: "This run only", attribute: "A custom attribute" },
    saveKey: "Field name",
    saveKeyHint: "Lower-case letters, numbers and _.",
    choices: "Choices",
    choicesHint: "One per line, up to {{max}}.",
    personalData: "Personal details are saved to the visitor or lead and covered by your privacy notice.",
    required: "The visitor must answer",
    requiredHint: "Turn off to let them skip.",
    skipIfKnown: "Skip if we already know it",
    skipIfKnownHint: "Returning visitors aren't asked twice.",
    answerIntro: "Answers only from your documents, with sources. If it can't, the run continues on the exit you choose.",
    guidance: "Extra guidance",
    guidanceHint: "Tell the assistant what to stress. It still answers only from your documents.",
    exits: "What happens next",
    exitContinue: "Continue with the next step",
    exitsHint: "With no path chosen, the run goes on with the next step, or ends if there is none.",
    bookingAction: "Booking action",
    bookingActionHint: "Hours, form fields and the calendar invite come from the action.",
    leadAction: "Lead form",
    leadActionHint: "The fields come from the Capture lead action.",
    actionMissing: "Action not found",
    noActions: "There is nothing to choose from yet.",
    openActions: "Open Actions",
    handTo: "Hand over to",
    handToHint: "The default follows your routing rules.",
    handTargets: { default: "The default routing", team: "A team", user: "A person" },
    handReason: "Note for your team",
    handReasonHint: "Staff see this when they open the conversation.",
    replyTimeHelp: "Tells the visitor when to expect a reply, based on your office hours. Nothing to set.",
  },
  conditions: {
    groups: { ...workflowsP3.conditionGroups, ...workflowsP4.conditionGroups, ...workflowsConnectorStep.conditionGroups, ...workflowsService.conditionGroups, ...workflowsCustomer.conditionGroups },
    attributeGroups: workflowsP3.attributeGroups,
    choose: workflowsTriggers.conditionChoices.choose,
    choices: {
      event_changed_by: workflowsTriggers.conditionChoices.event_changed_by,
      source_notice: workflowsTriggers.conditionChoices.source_notice,
      source_run_kind: workflowsTriggers.conditionChoices.source_run_kind,
      ...workflowsService.conditionChoices,
      ...workflowsCustomer.conditionChoices,
    },
    add: "Add condition",
    addGroup: "Add an OR group",
    and: "and",
    or: "or",
    remove: "Remove condition",
    field: "Field",
    operator: "Operator",
    value: "Value",
    listHint: "One per line.",
    yes: "Yes",
    no: "No",
    fields: {
      ...workflowsP3.conditionFields,
      ...workflowsTriggers.conditionFields,
      ...workflowsService.conditionFields,
      ...workflowsCustomer.conditionFields,
      conversation_channel: "Channel",
      conversation_agent_id: "Agent",
      conversation_status: "Conversation status",
      conversation_tags: "Conversation tags",
      conversation_language: "Language",
      conversation_topic: "Topic",
      conversation_assigned: "Has an assignee",
      conversation_team_id: "Team",
      conversation_message_count: "Number of messages",
      message_text: "Message text",
      message_role: "Message from",
      lead_stage: "Lead stage",
      lead_interest: "Lead interest",
      lead_source: "Lead source",
      lead_has_owner: "Lead has an owner",
      booking_service: "Booking service",
      booking_status: "Booking status",
      visitor_email_known: "Visitor email known",
      visitor_verified: "Visitor verified",
      customer_tier: "Customer tier",
      customer_account_status: "Customer account status",
      time_office_hours: "Within office hours",
      time_weekday: "Weekday",
      time_hour: "Hour (0–23)",
      event_reason: "Reason",
      intent_topic: "Detected topic",
      intent_action: "Detected intent",
    },
    operators: {
      is: "is",
      is_not: "is not",
      is_any_of: "is any of",
      is_none_of: "is none of",
      contains: "contains",
      not_contains: "does not contain",
      contains_any_of: "contains any of",
      starts_with: "starts with",
      is_set: "is set",
      is_not_set: "is not set",
      gt: "is more than",
      lt: "is less than",
    },
  },
  runs: {
    ...workflowsP4.runs,
    title: "Runs",
    empty: "No runs yet. Runs appear here when the trigger fires.",
    started: "Started",
    status: "Status",
    path: "Path",
    version: "Version",
    view: "View",
    statuses: { running: "Running", waiting: "Waiting", delegated: "With the assistant", completed: "Completed", failed: "Failed", cancelled: "Cancelled", skipped: "Not started" },
    endedReason: "Ended because",
    reasons: {
      ...workflowsVerify.runs.reasons,
      ...workflowsContent.reasons,
      endpoint_unavailable: "The endpoint is unavailable: it is turned off or deleted.",
      too_many_webhooks: "This run already sent the most webhooks it may.",
      body_not_json_object: "The message body is not a JSON object.",
    },
    endedReasons: {
      ...workflowsP4.runs.endedReasons,
      ...workflowsVerify.runs.endedReasons,
      finished: "It reached the end.",
      handed_off: "It was handed to a person.",
      typed_text: "The visitor typed their own message.",
      invalid_answers: "The answers did not fit the question.",
      staff_replied: "A team member replied.",
      takeover: "A team member took over.",
      resolved: "The conversation was resolved.",
      closed: "The conversation was closed.",
      expired: "The run expired.",
      failed: "A step failed.",
      interrupted: "A wait was cut short.",
      quota: "The monthly workflow run limit was used up, so this run did not start.",
      plan: "Your plan no longer includes this trigger, so this run did not start.",
    },
    outcomes: { done: "Done", skipped: "Skipped", failed: "Failed" },
    detailTitle: "Run",
    openConversation: "Open conversation",
    openLead: "Open lead",
    error: "Reason",
    loadMore: "Older runs",
    detailFailed: "The run could not be loaded",
    detailFailedBody: "Check your connection, then try again.",
  },
  overview: {
    ...workflowsP4.overview,
    range: "Last {{days}} days",
    sent: "Sent",
    sentHint: "runs started",
    completed: "Completed",
    completedHint: "reached the end",
    failed: "Failed",
    failedHint: "a step failed",
    steps: "Steps",
    reached: "Reached",
    failedSteps: "Failed",
    noData: "No runs in this period yet.",
  },
  hook: workflowsP4.hook,
  goal: workflowsP4.goal,
  canvas: workflowsP4.canvas,
  connectors: { ...workflowsConnectors, ...workflowsConversation.connectors },
  connector: workflowsConnectorStep.connector,
  versions: workflowsP4.versions,
  analysis: { ...workflowsP4.analysis, outcomes: { ...workflowsP4.analysis.outcomes, ...workflowsVerify.analysis.outcomes, ...workflowsContent.outcomes } },
  translations: { ...workflowsP4.translations, kinds: { ...workflowsP4.translations.kinds, ...workflowsContent.translationKinds } },
  setup: workflowsSetup,
  addStep: workflowsAddStep,
  extras: workflowsExtras,
  attributeValues: workflowsTriggers.attributeValues,
  article: workflowsContent.article,
  summaryNote: workflowsContent.summaryNote,
  inbox: workflowsP4.inbox,
  defaults: { ...workflowsDefaults, service: workflowsService.list },
  service: workflowsService.service,
  system: workflowsSystem.system,
  regression: workflowsRegression,
} as const;
